import { useCallback, useEffect, useRef, useState } from 'react';

export type MicrophonePermissionState = 'unknown' | 'checking' | 'allowed' | 'blocked';

type UseMicrophonePermission = {
	state: MicrophonePermissionState;
	/** Asks the browser for the microphone. Shows the prompt unless the user already decided. */
	request: () => Promise<void>;
};

/**
 * Requests microphone access when the page loads and tracks the result.
 *
 * Reports `blocked` both for an explicit denial and for a dismissed prompt, since both reject with
 * `NotAllowedError`. `request` is the retry for a user who missed the prompt. Once the user has
 * denied it for good the browser will not prompt again, and the page can only wait for a change.
 */
export const useMicrophonePermission = (): UseMicrophonePermission => {
	const [state, setState] = useState<MicrophonePermissionState>('unknown');
	const isMountedRef = useRef(true);

	const request = useCallback(async (): Promise<void> => {
		if (!navigator.mediaDevices?.getUserMedia) {
			setState('blocked');
			return;
		}

		setState('checking');
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			// Only the grant matters here; release the device straight away.
			stream.getTracks().forEach((track) => track.stop());
			if (isMountedRef.current) {
				setState('allowed');
			}
		} catch {
			if (isMountedRef.current) {
				setState('blocked');
			}
		}
	}, []);

	useEffect(() => {
		isMountedRef.current = true;
		void request();

		return (): void => {
			isMountedRef.current = false;
		};
	}, [request]);

	// Picks up a change made outside this page, such as in the browser's site settings.
	useEffect(() => {
		if (!navigator.permissions?.query) {
			return undefined;
		}

		let permissionStatus: PermissionStatus | undefined;
		let isCancelled = false;

		const syncFromPermission = (): void => {
			if (permissionStatus?.state === 'granted') {
				setState('allowed');
			} else if (permissionStatus?.state === 'denied') {
				setState('blocked');
			}
		};

		navigator.permissions
			.query({ name: 'microphone' as PermissionName })
			.then((status) => {
				if (isCancelled) {
					return;
				}
				permissionStatus = status;
				status.addEventListener('change', syncFromPermission);
			})
			.catch(() => {
				// Not every browser can query the microphone; the request result still drives the state.
			});

		return (): void => {
			isCancelled = true;
			permissionStatus?.removeEventListener('change', syncFromPermission);
		};
	}, []);

	return { state, request };
};
